/**
 * useTasksMine · vista "Mis tareas" cross-área.
 *
 * Devuelve mis tareas con buckets calculados de urgencia:
 *  - overdue   · due_at < now AND status != done/archived
 *  - today     · due_at en la franja [00:00 hoy, 23:59 hoy]
 *  - week      · due_at en [mañana, +6d]
 *  - later     · due_at > +6d
 *  - undated   · sin due_at
 *  - done      · status = done (colapsado por default en UI)
 *
 * "Mías" = soy created_by O hay una row en task_assignments con user_id=auth.uid()
 * AND accepted_at IS NOT NULL.
 *
 * Hacemos 2 queries paralelas y mergeamos en cliente (sin duplicar).
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';
import type { TasksRow } from '@/types/database.types';

export type MineBucket = 'overdue' | 'today' | 'week' | 'later' | 'undated' | 'done';

export const MINE_BUCKET_ORDER: MineBucket[] = [
  'overdue', 'today', 'week', 'later', 'undated', 'done',
];

export const MINE_BUCKET_LABEL: Record<MineBucket, string> = {
  overdue: 'Vencidas',
  today: 'Hoy',
  week: 'Esta semana',
  later: 'Más adelante',
  undated: 'Sin fecha',
  done: 'Completadas',
};

export const MINE_BUCKET_ACCENT: Record<MineBucket, string> = {
  overdue: 'var(--err)',
  today: 'var(--warn)',
  week: 'var(--info)',
  later: 'var(--brand)',
  undated: 'var(--fg-muted)',
  done: 'var(--ok)',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

function endOfToday(): Date {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
}

function endOfWeek(): Date {
  // 6 días desde mañana (inclusive)
  const d = endOfToday();
  d.setDate(d.getDate() + 6);
  return d;
}

export function bucketize(task: TasksRow, now = new Date()): MineBucket {
  if (task.status === 'done') return 'done';
  if (!task.due_at) return 'undated';
  const due = new Date(task.due_at);
  if (due.getTime() < now.getTime() && task.status !== 'archived') return 'overdue';
  const todayEnd = endOfToday();
  if (due.getTime() <= todayEnd.getTime()) return 'today';
  const weekEnd = endOfWeek();
  if (due.getTime() <= weekEnd.getTime()) return 'week';
  return 'later';
}

export function useTasksMine() {
  const { session } = useSupabase();
  const userId = session?.user?.id ?? null;

  const query = useQuery({
    queryKey: ['core', 'tasks-mine', userId],
    enabled: !!userId,
    queryFn: async (): Promise<TasksRow[]> => {
      if (!userId) return [];
      // Query 1 · tasks que creé yo y no están deleted (sólo top-level · sin subtareas)
      const own = db
        .from('tasks')
        .select('*')
        .eq('created_by', userId)
        .is('deleted_at', null)
        .is('parent_task_id', null)
        .neq('status', 'archived')
        .order('due_at', { ascending: true, nullsFirst: false });

      // Query 2 · tasks donde estoy asignado y la asignación está aceptada.
      // Las subtareas las filtramos client-side abajo.
      const assigned = db
        .from('task_assignments')
        .select('task:tasks(*)')
        .eq('user_id', userId)
        .eq('declined', false)
        .not('accepted_at', 'is', null);

      const [ownRes, assignedRes] = await Promise.all([own, assigned]);
      if (ownRes.error) throw ownRes.error;
      if (assignedRes.error) throw assignedRes.error;

      const ownRows = (ownRes.data ?? []) as TasksRow[];
      const assignedRows = (
        (assignedRes.data ?? []) as Array<{ task: TasksRow | null }>
      )
        .map((r) => r.task)
        .filter(
          (t): t is TasksRow =>
            t !== null &&
            t.status !== 'archived' &&
            !(t as { deleted_at: string | null }).deleted_at &&
            !t.parent_task_id, // filtrar subtareas del view principal
        );

      const dedupe = new Map<string, TasksRow>();
      for (const t of ownRows) dedupe.set(t.id, t);
      for (const t of assignedRows) dedupe.set(t.id, t);
      return [...dedupe.values()];
    },
    staleTime: 30_000,
  });

  const buckets = useMemo(() => {
    const now = new Date();
    const map = new Map<MineBucket, TasksRow[]>();
    for (const b of MINE_BUCKET_ORDER) map.set(b, []);
    for (const t of query.data ?? []) {
      const bucket = bucketize(t, now);
      map.get(bucket)!.push(t);
    }
    // sort por priority (urgent → low) dentro de cada bucket
    const pOrder: Record<string, number> = { urgent: 0, high: 1, med: 2, low: 3 };
    for (const list of map.values()) {
      list.sort((a, b) => (pOrder[a.priority] ?? 9) - (pOrder[b.priority] ?? 9));
    }
    return map;
  }, [query.data]);

  return {
    tasks: query.data ?? [],
    buckets,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
